import { useCallback, useMemo } from "react"

import type {
	AiChatSettingsTranslation,
	Changelog,
	ChangelogTranslation,
	HelpCenterArticle,
	HelpCenterArticleTranslation,
	HelpCenterGroup,
	HelpCenterGroupTranslation,
	HelpCenterHomeTranslation,
	Issue,
	IssueTranslation,
	Project,
	ProjectTranslation,
} from "@productlane/zero"

import type { Language } from "../screens/settings/settings-pages/language-selector"
import type { ReadonlyJSONValue } from "@rocicorp/zero"
import { fallbackTranslationId } from "./translation-fallback-id"
import { useZeroWorkspace } from "./zero"

export interface BaseTranslation {
	id: string
	language: string
	workspaceId: string
	createdAt?: number
	updatedAt?: number
	title?: string | null
	description?: string | null
	content?: ReadonlyJSONValue | string | null
	summary?: string | null
	name?: string | null
	notes?: ReadonlyJSONValue | null
}

interface UseItemTranslationsProps<
	TTranslation extends BaseTranslation,
	TItem extends {
		id: string
		workspaceId: string
		translations?: readonly TTranslation[]
	},
> {
	item: TItem
	selectedLanguage: Language
	itemTranslationComparator: (translation: TTranslation, item: TItem) => boolean
	createFallbackTranslation: (item: TItem, language: string) => TTranslation
}

/**
 * Whether a row carries nothing a reader could see. The v2 API leaves the base
 * row empty when it writes a non-default language, so the base is not always a
 * usable fallback.
 */
function isBlankTranslation(translation: BaseTranslation): boolean {
	const text = [translation.title, translation.name, translation.description]
		.map((value) => value?.trim() ?? "")
		.join("")
	if (text.length > 0) return false

	const { content } = translation
	if (content === null || content === undefined) return true
	if (typeof content === "string") {
		const trimmed = content.trim()
		return trimmed === "" || trimmed === "null"
	}
	return Object.keys(content as object).length === 0
}

export function useItemTranslations<
	TTranslation extends BaseTranslation,
	TItem extends {
		id: string
		workspaceId: string
		translations?: readonly TTranslation[]
	},
>({
	item,
	selectedLanguage,
	itemTranslationComparator,
	createFallbackTranslation,
}: UseItemTranslationsProps<TTranslation, TItem>) {
	const workspace = useZeroWorkspace()

	const defaultLanguage = workspace?.defaultLanguage ?? "en"

	const itemTranslations = useMemo(() => {
		return (item.translations ?? []).filter((t) =>
			itemTranslationComparator(t, item),
		)
	}, [item, itemTranslationComparator])

	const defaultTranslation = useMemo(() => {
		return itemTranslations.find((t) => t.language === defaultLanguage)
	}, [itemTranslations, defaultLanguage])

	const selectedTranslation = useMemo<TTranslation>(() => {
		const found = itemTranslations.find(
			(t) => t.language === selectedLanguage.code,
		)

		if (found) return found

		const fromBase = createFallbackTranslation(item, selectedLanguage.code)
		if (!isBlankTranslation(fromBase)) return fromBase

		const source =
			itemTranslations.find((t) => t.language === defaultLanguage) ??
			itemTranslations.find((t) => !isBlankTranslation(t))
		if (!source) return fromBase

		return { ...source, id: fromBase.id, language: selectedLanguage.code }
	}, [
		itemTranslations,
		createFallbackTranslation,
		item,
		selectedLanguage.code,
		defaultLanguage,
	])

	return {
		itemTranslations,
		defaultTranslation,
		selectedTranslation,
		defaultLanguage,
	}
}

interface TranslationHookConfig<
	TItem extends {
		id: string
		workspaceId: string
		translations?: readonly TTranslation[]
	},
	TTranslation extends BaseTranslation,
> {
	foreignKeyField: keyof TTranslation
	createFallback: (
		item: TItem,
		language: string,
		workspaceId: string,
	) => TTranslation
}

export function createTranslationHook<
	TItem extends {
		id: string
		workspaceId: string
		translations?: readonly TTranslation[]
	},
	TTranslation extends BaseTranslation,
>(config: TranslationHookConfig<TItem, TTranslation>) {
	return function useEntityTranslations(
		item: TItem,
		selectedLanguage: Language,
	) {
		const comparator = useCallback(
			(t: TTranslation, i: TItem) =>
				t[config.foreignKeyField] ===
				(i.id as TTranslation[keyof TTranslation]),
			[],
		)

		const fallbackCreator = useCallback(
			(i: TItem, lang: string) => config.createFallback(i, lang, i.workspaceId),
			[],
		)

		return useItemTranslations({
			item,
			selectedLanguage,
			itemTranslationComparator: comparator,
			createFallbackTranslation: fallbackCreator,
		})
	}
}

type HelpCenterArticleWithTranslations = HelpCenterArticle & {
	translations?: readonly HelpCenterArticleTranslation[]
}

type HelpCenterGroupWithTranslations = HelpCenterGroup & {
	translations?: readonly HelpCenterGroupTranslation[]
}

export const useHelpCenterArticleTranslations = createTranslationHook<
	HelpCenterArticleWithTranslations,
	HelpCenterArticleTranslation
>({
	foreignKeyField: "helpCenterArticleId",
	createFallback: (article, language, workspaceId) => {
		let contentStr = ""
		if (article.content) {
			if (typeof article.content === "string") {
				contentStr = article.content
			} else if (
				typeof article.content === "object" &&
				Object.keys(article.content).length > 0
			) {
				contentStr = JSON.stringify(article.content)
			}
		}
		return {
			id: fallbackTranslationId(article.id, language),
			language,
			workspaceId,
			helpCenterArticleId: article.id,
			title: article.title ?? "",
			content: contentStr,
			summary: article.summary ?? null,
			imageUrl: article.imageUrl ?? null,
			translationStatus: null,
			createdAt: Date.now(),
			updatedAt: Date.now(),
		}
	},
})

export const useHelpCenterGroupTranslations = createTranslationHook<
	HelpCenterGroupWithTranslations,
	HelpCenterGroupTranslation
>({
	foreignKeyField: "helpCenterGroupId",
	createFallback: (group, language, workspaceId) => ({
		id: fallbackTranslationId(group.id, language),
		language,
		workspaceId,
		helpCenterGroupId: group.id,
		title: group.name ?? "",
		description: "",
		createdAt: Date.now(),
		updatedAt: Date.now(),
	}),
})

type ProjectWithTranslations = Project & {
	translations?: readonly ProjectTranslation[]
}

type IssueWithTranslations = Issue & {
	translations?: readonly IssueTranslation[]
}

export const useProjectTranslations = createTranslationHook<
	ProjectWithTranslations,
	ProjectTranslation
>({
	foreignKeyField: "projectId",
	createFallback: (project, language, workspaceId) => ({
		id: fallbackTranslationId(project.id, language),
		language,
		workspaceId,
		projectId: project.id,
		name: project.publicTitle ?? project.name ?? "",
		description: project.publicDescription ?? project.description ?? "",
		summary: "",
		createdAt: Date.now(),
		updatedAt: Date.now(),
	}),
})

export const useIssueTranslations = createTranslationHook<
	IssueWithTranslations,
	IssueTranslation
>({
	foreignKeyField: "issueId",
	createFallback: (issue, language, workspaceId) => ({
		id: fallbackTranslationId(issue.id, language),
		language,
		workspaceId,
		issueId: issue.id,
		title: issue.publicTitle ?? issue.title ?? "",
		description: issue.publicDescription ?? issue.description ?? "",
		createdAt: Date.now(),
		updatedAt: Date.now(),
	}),
})

type ChangelogWithTranslations = Changelog & {
	translations?: readonly ChangelogTranslation[]
}

export const useChangelogTranslations = createTranslationHook<
	ChangelogWithTranslations,
	ChangelogTranslation
>({
	foreignKeyField: "changelogId",
	createFallback: (changelog, language, workspaceId) => ({
		id: fallbackTranslationId(changelog.id, language),
		language,
		workspaceId,
		changelogId: changelog.id,
		title: changelog.title ?? "",
		notes: changelog.notes ?? null,
		imageUrl: changelog.imageUrl,
		imageBlurhash: changelog.imageBlurhash,
		coverVideoUrl: changelog.coverVideoUrl,
		translationStatus: null,
		createdAt: Date.now(),
		updatedAt: Date.now(),
	}),
})

interface WorkspaceHomeTranslationItem {
	id: string
	workspaceId: string
	helpCenterTitle: string
	helpCenterSummary: string
	translations?: readonly HelpCenterHomeTranslation[]
}

export const useHelpCenterHomeTranslations = createTranslationHook<
	WorkspaceHomeTranslationItem,
	HelpCenterHomeTranslation
>({
	foreignKeyField: "workspaceId",
	createFallback: (workspace, language, workspaceId) => ({
		id: fallbackTranslationId(workspace.id, language),
		language,
		workspaceId,
		title: workspace.helpCenterTitle ?? "",
		summary: workspace.helpCenterSummary ?? "",
		createdAt: Date.now(),
		updatedAt: Date.now(),
	}),
})

function isTipTapContentEmpty(notes: ReadonlyJSONValue | null): boolean {
	if (notes == null) return true
	if (typeof notes !== "object" || Array.isArray(notes)) return true
	const doc = notes as Record<string, ReadonlyJSONValue>
	const content = doc.content
	if (!Array.isArray(content) || content.length === 0) return true
	return !content.some((node) => hasTextContent(node as ReadonlyJSONValue))
}

function hasTextContent(node: ReadonlyJSONValue): boolean {
	if (typeof node !== "object" || node == null || Array.isArray(node))
		return false
	const n = node as Record<string, ReadonlyJSONValue>
	if (
		n.type === "text" &&
		typeof n.text === "string" &&
		n.text.trim().length > 0
	)
		return true
	if (n.type === "image" || n.type === "video" || n.type === "embed")
		return true
	if (Array.isArray(n.content)) {
		return n.content.some((child) => hasTextContent(child as ReadonlyJSONValue))
	}
	return false
}

export function isChangelogTranslationEmpty(
	translation: ChangelogTranslation | undefined,
	fallbackChangelog?: { title: string; notes: ReadonlyJSONValue | null },
): boolean {
	// No translation record exists
	if (!translation || translation.id.startsWith("temp-")) {
		// For default language, content may live in the base changelog
		if (fallbackChangelog) {
			const hasTitle = !!fallbackChangelog.title?.trim()
			const hasNotes = !isTipTapContentEmpty(fallbackChangelog.notes)
			return !hasTitle && !hasNotes
		}
		return true
	}

	const hasTitle = !!translation.title?.trim()
	const hasNotes = !isTipTapContentEmpty(translation.notes)
	return !hasTitle && !hasNotes
}

export function getEmptyChangelogLanguages(
	changelog: {
		title: string
		notes: ReadonlyJSONValue | null
		translations?: readonly ChangelogTranslation[]
	},
	enabledLanguages: string[],
	defaultLanguage: string,
): string[] {
	const allLanguages = [...new Set([defaultLanguage, ...enabledLanguages])]

	return allLanguages.filter((langCode) => {
		const translation = changelog.translations?.find(
			(t) => t.language === langCode,
		)
		const isDefault = langCode === defaultLanguage
		return isChangelogTranslationEmpty(
			translation,
			isDefault ? changelog : undefined,
		)
	})
}

interface WorkspaceAiChatSettingsTranslationItem {
	id: string
	workspaceId: string
	welcomeMessage: string
	exampleQuestions: string[]
	translations?: readonly AiChatSettingsTranslation[]
}

export const useAiChatSettingsTranslations = createTranslationHook<
	WorkspaceAiChatSettingsTranslationItem,
	AiChatSettingsTranslation
>({
	foreignKeyField: "workspaceId",
	createFallback: (settings, language, workspaceId) => ({
		id: fallbackTranslationId(settings.id, language),
		language,
		workspaceId,
		welcomeMessage: settings.welcomeMessage ?? null,
		exampleQuestions: settings.exampleQuestions ?? [],
		createdAt: Date.now(),
		updatedAt: Date.now(),
		version: 1,
		isDeleted: false,
	}),
})
